import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const CATS = ['information','maintenance','warning','critical'];
const CAT_COLOR = { information:'var(--accent)', maintenance:'var(--amber)', warning:'var(--orange)', critical:'var(--red)' };
const AUD = [['all','All Users'],['it_staff','IT Staff Only'],['teaching_staff','Teaching Staff Only']];
const BLANK = { title:'', description:'', category:'information', priority:'Normal', audience:'all',
  all_campuses:true, start_date:'', end_date:'', pinned:false, notify:false, campus_ids:[] };

export default function Announcements() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get('/announcements/all').then(r => setRows(r.data)).catch(()=>{}); }, []);
  useEffect(() => { load(); api.get('/master/campuses').then(r => setCampuses(r.data.filter(c=>c.active))).catch(()=>{}); }, [load]);
  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;

  const openNew = () => setModal({ ...BLANK });
  const openEdit = a => setModal({ ...BLANK, ...a, start_date:(a.start_date||'').slice(0,10), end_date:(a.end_date||'').slice(0,10), campus_ids:a.campus_ids||[] });
  const set = (k,v) => setModal(m => ({ ...m, [k]:v }));
  const save = async () => {
    if (!modal.title.trim()) return;
    setBusy(true);
    try {
      if (modal.id) await api.put(`/announcements/${modal.id}`, modal);
      else await api.post('/announcements', modal);
      setModal(null); load();
    } finally { setBusy(false); }
  };
  const del = async a => { if (window.confirm('Delete this announcement?')) { await api.delete(`/announcements/${a.id}`); load(); } };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:16 }}>
        <div><h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>IT Announcements</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>Post information, maintenance, warning and critical notices to staff.</p></div>
        <button className="btn btn-primary" onClick={openNew}>+ New Announcement</button>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No announcements yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Title</th><th>Category</th><th>Audience</th><th>Window</th><th>Flags</th><th></th></tr></thead>
            <tbody>{rows.map(a => (
              <tr key={a.id}>
                <td style={{ fontSize:13, fontWeight:500 }}>{a.title}</td>
                <td><span style={{ fontSize:11, color:CAT_COLOR[a.category], border:`1px solid ${CAT_COLOR[a.category]}`, borderRadius:10, padding:'1px 8px' }}>{a.category}</span></td>
                <td style={{ fontSize:12 }}>{(AUD.find(x=>x[0]===a.audience)||[])[1]}{a.all_campuses?' · All campuses':' · Specific'}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{a.start_date?new Date(a.start_date).toLocaleDateString():'—'} → {a.end_date?new Date(a.end_date).toLocaleDateString():'—'}</td>
                <td style={{ fontSize:12 }}>{a.pinned?'📌 ':''}{a.notify?'🔔 ':''}{!a.active?'(inactive)':''}</td>
                <td><div className="flex-gap" style={{ gap:6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(a)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => del(a)}>Delete</button>
                </div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(null)}>
          <div className="modal" style={{ maxWidth:560 }}>
            <div className="modal-header"><span className="modal-title">{modal.id?'Edit':'New'} Announcement</span><button className="modal-close" onClick={() => setModal(null)}>×</button></div>
            <div className="form-group"><label>Title *</label><input value={modal.title} onChange={e => set('title',e.target.value)} /></div>
            <div className="form-group"><label>Description</label><textarea rows={3} value={modal.description||''} onChange={e => set('description',e.target.value)} /></div>
            <div className="form-row">
              <div className="form-group"><label>Category</label>
                <select value={modal.category} onChange={e => set('category',e.target.value)}>{CATS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              <div className="form-group"><label>Priority</label>
                <select value={modal.priority} onChange={e => set('priority',e.target.value)}><option>Low</option><option>Normal</option><option>High</option></select></div>
              <div className="form-group"><label>Audience</label>
                <select value={modal.audience} onChange={e => set('audience',e.target.value)}>{AUD.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Start Date</label><input type="date" value={modal.start_date} onChange={e => set('start_date',e.target.value)} /></div>
              <div className="form-group"><label>End Date</label><input type="date" value={modal.end_date} onChange={e => set('end_date',e.target.value)} /></div>
            </div>
            <div className="form-group">
              <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, textTransform:'none', letterSpacing:0 }}>
                <input type="checkbox" checked={modal.all_campuses} onChange={e => set('all_campuses',e.target.checked)} style={{ width:15, height:15 }} /> All campuses
              </label>
            </div>
            {!modal.all_campuses && (
              <div className="form-group"><label>Campuses</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {campuses.map(c => { const on = (modal.campus_ids||[]).includes(c.id);
                    return <button key={c.id} type="button" className={`btn btn-sm ${on?'btn-primary':'btn-secondary'}`}
                      onClick={() => set('campus_ids', on ? modal.campus_ids.filter(x=>x!==c.id) : [...(modal.campus_ids||[]), c.id])}>{c.name}</button>; })}
                </div>
              </div>
            )}
            <div className="flex-gap" style={{ gap:18, marginTop:4 }}>
              <label className="opt" style={{ display:'flex', alignItems:'center', gap:6, textTransform:'none', letterSpacing:0, fontSize:13 }}>
                <input type="checkbox" checked={modal.pinned} onChange={e => set('pinned',e.target.checked)} /> Pin to dashboard</label>
              <label className="opt" style={{ display:'flex', alignItems:'center', gap:6, textTransform:'none', letterSpacing:0, fontSize:13 }}>
                <input type="checkbox" checked={modal.notify} onChange={e => set('notify',e.target.checked)} /> Send notification (email)</label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy || !modal.title.trim()}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
